// File upload utility for saving files to public folder
import { API_BASE_URL } from '../config/api';

// Upload file to server and get public path
export const uploadFileToPublic = async (file, folder = 'uploads') => {
  try {
    console.log('🔄 uploadFileToPublic called with:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      folder: folder
    });

    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error('Please select an image file');
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      throw new Error('File size must be less than 5MB');
    }

    // Create FormData for file upload
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'image');
    formData.append('folder', folder);

    console.log('📤 Sending request to:', `${API_BASE_URL}/upload-file`);
    console.log('🔑 Auth token:', localStorage.getItem('authToken') ? 'Present' : 'Missing');

    // Try multiple upload endpoints
    const uploadEndpoints = [
      `${API_BASE_URL}/upload-file`,
      `${API_BASE_URL}/file-upload`,
      `${API_BASE_URL}/upload`,
      `${API_BASE_URL}/files/upload`
    ];

    let response = null;
    let lastError = null;

    for (const endpoint of uploadEndpoints) {
      try {
        console.log('📤 Trying upload endpoint:', endpoint);
        
        const token = localStorage.getItem('authToken');
        const headers = {};
        
        // Only add Authorization header if token exists
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        // Note: Don't set Content-Type for FormData - browser sets it automatically with boundary
        response = await fetch(endpoint, {
          method: 'POST',
          headers: headers,
          body: formData
        });

        if (response.ok) {
          console.log('✅ Upload successful with endpoint:', endpoint);
          break;
        } else {
          const errorText = await response.text().catch(() => 'Unable to read error response');
          console.log(`❌ Endpoint ${endpoint} failed with status:`, response.status);
          console.log(`❌ Error response:`, errorText);
          lastError = new Error(`Endpoint ${endpoint} failed: ${response.status} - ${errorText}`);
        }
      } catch (error) {
        console.log(`❌ Endpoint ${endpoint} error:`, error.message);
        lastError = error;
        response = null;
      }
    }

    if (!response || !response.ok) {
      throw lastError || new Error('All upload endpoints failed');
    }

    console.log('📡 Upload response status:', response.status);
    console.log('📡 Upload response ok:', response.ok);

    // Parse response
    let result;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      result = await response.json();
    } else {
      const textResponse = await response.text();
      console.warn('⚠️ Response is not JSON, received text:', textResponse);
      // Try to parse as JSON anyway
      try {
        result = JSON.parse(textResponse);
      } catch (e) {
        throw new Error(`Invalid response format. Expected JSON but got: ${contentType || 'text'}`);
      }
    }
    
    console.log('📡 Upload API response:', result);
    
    if (result.success) {
      // Handle different possible response structures
      let filePath = null;
      
      if (result.data && result.data.file_path) {
        filePath = result.data.file_path;
      } else if (result.data && result.data.path) {
        filePath = result.data.path;
      } else if (result.data && result.data.url) {
        filePath = result.data.url;
      } else if (result.file_path) {
        filePath = result.file_path;
      } else if (result.path) {
        filePath = result.path;
      } else if (result.url) {
        filePath = result.url;
      } else if (result.data) {
        // If data is a string, use it directly
        filePath = typeof result.data === 'string' ? result.data : null;
      }
      
      if (filePath) {
        console.log('✅ Extracted file path:', filePath);
        return filePath;
      } else {
        console.error('❌ Could not extract file path from response:', result);
        throw new Error('Invalid response structure - no file path found');
      }
    } else {
      throw new Error(result.message || 'Upload failed');
    }
  } catch (error) {
    console.error('File upload error:', error);
    
    // Fallback: Create a local blob URL for development
    console.log('🔄 API upload failed, using local fallback...');
    console.log('⚠️ Note: This is a local fallback. File will not persist on server.');
    console.warn('⚠️ File upload to server failed. Using local preview. Please check your connection and try again.');
    
    const fallbackUrl = await createLocalFileFallback(file, folder);
    
    return fallbackUrl;
  }
};

// Fallback function to create local file simulation
const createLocalFileFallback = async (file, folder = 'uploads') => {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        // Create a blob URL for local use
        const blob = new Blob([e.target.result], { type: file.type });
        const blobUrl = URL.createObjectURL(blob);
        
        // Store the blob URL in sessionStorage for persistence
        const fileKey = `temp_file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem(fileKey, JSON.stringify({
          blobUrl: blobUrl,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          folder: folder,
          uploadedAt: new Date().toISOString()
        }));
        
        console.log('✅ Local file fallback created:', fileKey);
        resolve(blobUrl);
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      
      reader.readAsArrayBuffer(file);
      
    } catch (error) {
      reject(error);
    }
  });
};

// Upload site logo specifically
export const uploadSiteLogo = async (file) => {
  return uploadFileToPublic(file, 'sites');
};

// Upload PDF file specifically to api/upload-file endpoint
export const uploadPdfFile = async (file, folder = 'pdfs') => {
  try {
    console.log('🔄 uploadPdfFile called with:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      folder: folder
    });

    // Validate file type (PDF only)
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      throw new Error('Please select a PDF file');
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      throw new Error('File size must be less than 10MB');
    }

    // Create FormData for file upload
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'pdf');
    formData.append('folder', folder);

    const token = localStorage.getItem('authToken');
    const headers = {};
    
    // Only add Authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    console.log('📤 Uploading PDF to:', `${API_BASE_URL}/upload-file`);
    
    const response = await fetch(`${API_BASE_URL}/upload-file`, {
      method: 'POST',
      headers: headers,
      body: formData
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unable to read error response');
      console.error('❌ PDF upload failed:', response.status, errorText);
      throw new Error(`Failed to upload PDF: ${response.status} - ${errorText}`);
    }

    // Parse response
    let result;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      result = await response.json();
    } else {
      const textResponse = await response.text();
      console.warn('⚠️ Response is not JSON, received text:', textResponse);
      try {
        result = JSON.parse(textResponse);
      } catch (e) {
        throw new Error(`Invalid response format. Expected JSON but got: ${contentType || 'text'}`);
      }
    }

    console.log('📡 PDF Upload API response:', result);

    // Handle different possible response structures
    let filePath = null;

    if (result.success) {
      if (result.data && result.data.file_path) {
        filePath = result.data.file_path;
      } else if (result.data && result.data.path) {
        filePath = result.data.path;
      } else if (result.data && result.data.url) {
        filePath = result.data.url;
      } else if (result.file_path) {
        filePath = result.file_path;
      } else if (result.path) {
        filePath = result.path;
      } else if (result.url) {
        filePath = result.url;
      } else if (result.data) {
        filePath = typeof result.data === 'string' ? result.data : null;
      }

      if (filePath) {
        console.log('✅ Extracted PDF file path:', filePath);
        return filePath;
      } else {
        console.error('❌ Could not extract file path from response:', result);
        throw new Error('Invalid response structure - no file path found');
      }
    } else {
      throw new Error(result.message || 'PDF upload failed');
    }
  } catch (error) {
    console.error('PDF upload error:', error);
    throw error; // Re-throw to let calling code handle it
  }
};

// Delete file from server
export const deleteFile = async (filePath) => {
  try {
    const response = await fetch(`${API_BASE_URL}/file-delete`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify({ file_path: filePath })
    });

    if (!response.ok) {
      throw new Error('Failed to delete file');
    }

    const result = await response.json();
    return result.success;
  } catch (error) {
    console.error('File deletion error:', error);
    throw error;
  }
};

// Get full URL for a file path
export const getFileUrl = (filePath) => {
  if (!filePath) return null;
  
  // If it's already a full URL, return as is
  if (filePath.startsWith('http://') || filePath.startsWith('https://') || filePath.startsWith('blob:')) {
    return filePath;
  }
  
  // Construct URL using the public API endpoint
  const baseUrl = API_BASE_URL.replace('/api', ''); // Remove /api from base URL
  return `${baseUrl}/api/public/sites/${filePath}`;
};

// Create a logo blob for Site logo
export const createSiteLogoBlob = (siteName = 'Site', size = 64, backgroundColor = '#3B82F6', textColor = '#FFFFFF') => {
  return new Promise((resolve, reject) => {
    try {
      // Create canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas size
      canvas.width = size;
      canvas.height = size;
      
      // Draw background circle
      ctx.fillStyle = backgroundColor;
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
      ctx.fill();
      
      // Draw text
      ctx.fillStyle = textColor;
      ctx.font = `bold ${size * 0.4}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      // Get first letter(s) of site name
      const initials = siteName
        .split(' ')
        .map(word => word.charAt(0).toUpperCase())
        .join('')
        .substring(0, 2);
      
      // Draw initials
      ctx.fillText(initials, size / 2, size / 2);
      
      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (blob) {
          const blobUrl = URL.createObjectURL(blob);
          resolve(blobUrl);
        } else {
          reject(new Error('Failed to create logo blob'));
        }
      }, 'image/png');
      
    } catch (error) {
      reject(error);
    }
  });
};

// Create a default site logo blob
export const createDefaultSiteLogo = async (siteName = 'Site') => {
  try {
    const logoUrl = await createSiteLogoBlob(siteName);
    return logoUrl;
  } catch (error) {
    console.error('Error creating default site logo:', error);
    return null;
  }
};

// Cleanup temporary blob URLs from sessionStorage
export const cleanupTempFiles = () => {
  try {
    const keys = Object.keys(sessionStorage);
    const tempFileKeys = keys.filter(key => key.startsWith('temp_file_'));
    
    tempFileKeys.forEach(key => {
      const fileData = JSON.parse(sessionStorage.getItem(key));
      if (fileData && fileData.blobUrl) {
        URL.revokeObjectURL(fileData.blobUrl);
        sessionStorage.removeItem(key);
      }
    });
    
    console.log(`🧹 Cleaned up ${tempFileKeys.length} temporary files`);
  } catch (error) {
    console.error('Error cleaning up temp files:', error);
  }
};
